/**
 * Submit orchestration for the multi-draft activity creation flow.
 *
 * Extracted from CreateActivityPage so the retry semantics are testable without rendering
 * the page. The rules this encodes all exist to make a second submit attempt safe after a
 * partial failure:
 *
 * - An activity that was created is recorded immediately and never created again, even if
 *   the response carried no usable id (see UNRESOLVED_ACTIVITY_ID).
 * - Attachments that uploaded successfully are dropped from the pending set, so a retry
 *   cannot upload them twice — including when a *later* draft aborts the run.
 * - Attachments that failed are kept, so the user can retry them against the activity that
 *   already exists rather than starting over.
 */

/**
 * Sentinel stored for a draft whose activity was created but whose id the backend response
 * did not contain. It marks the draft as persisted — so a retry cannot duplicate it — while
 * remaining distinguishable from a real id, which is never negative.
 */
export const UNRESOLVED_ACTIVITY_ID = -1

export const UNRESOLVED_ACTIVITY_ATTACHMENT_MESSAGE =
  'Die Aktivität wurde erstellt, aber ihre ID wurde nicht zurückgegeben. Bitte hängen Sie die Datei direkt an die Aktivität an.'

export type FailedUpload = { name: string; message: string }

export type SubmitActivityDraftsOptions<D, A extends { file: File }> = {
  drafts: D[]
  /** Stable identity per draft, parallel to `drafts`. Falls back to the index if too short. */
  draftKeys: string[]
  attachmentsByDraft: A[][]
  /** Ids persisted by earlier attempts, keyed by draft key. Mutated in place as drafts persist. */
  createdActivityIds: Record<string, number>
  /** Throws to reject the draft; the run stops at that draft. */
  validateDraft: (draft: D) => void
  createActivity: (draft: D) => Promise<unknown>
  resolveActivityId: (created: unknown) => string | number | undefined
  uploadAttachment: (activityId: number, file: File) => Promise<unknown>
  describeUploadError: (reason: unknown) => string
  onUploadError?: (reason: unknown) => void
}

export type SubmitActivityDraftsResult<A> = {
  /** False when a draft threw; `error` then holds the cause for the caller to classify. */
  ok: boolean
  error?: unknown
  failedUploads: FailedUpload[]
  /** Attachments still pending per draft — what the caller should keep in form state. */
  remainingAttachmentsByDraft: A[][]
}

export async function submitActivityDrafts<D, A extends { file: File }>({
  drafts,
  draftKeys,
  attachmentsByDraft,
  createdActivityIds,
  validateDraft,
  createActivity,
  resolveActivityId,
  uploadAttachment,
  describeUploadError,
  onUploadError,
}: SubmitActivityDraftsOptions<D, A>): Promise<SubmitActivityDraftsResult<A>> {
  // Attachment uploads must not abort the submit: the activity is already persisted at
  // that point, so we collect failures and report them instead of throwing.
  const failedUploads: FailedUpload[] = []

  // Starts as the current selection and is narrowed to the failed files once a draft's
  // uploads settle, so successfully-uploaded files are never offered again. Drafts we never
  // reach (because an earlier one threw) keep their files untouched.
  const remainingAttachmentsByDraft: A[][] = drafts.map((_, i) => attachmentsByDraft[i] ?? [])

  try {
    for (let i = 0; i < drafts.length; i++) {
      const draft = drafts[i]
      // Falls back to the index only if the key array ever drifts out of sync, which
      // degrades to positional behaviour rather than silently reusing another draft's
      // activity.
      const draftKey = draftKeys[i] ?? `index-${i}`

      // 1) validate draft
      validateDraft(draft)

      // 2) create activity — skip drafts already persisted by a previous attempt
      let createdId: number | undefined = createdActivityIds[draftKey]

      if (createdId == null) {
        const created = await createActivity(draft)
        // 3) resolve created activity id. Record the outcome either way: the activity is
        // persisted at this point, so a retry must not create it again even when the
        // response carried no usable id.
        const resolvedId = resolveActivityId(created)
        createdId = resolvedId != null ? Number(resolvedId) : UNRESOLVED_ACTIVITY_ID
        createdActivityIds[draftKey] = createdId
      }

      // 4) upload attachments for THIS draft (if we have an id & files)
      const attachmentsForDraft = attachmentsByDraft[i] ?? []
      if (attachmentsForDraft.length === 0) continue

      if (createdId === UNRESOLVED_ACTIVITY_ID) {
        // The activity exists but we never learned its id, so there is nothing to upload
        // against and retrying cannot help. Report it and stop offering the files here —
        // the user can still attach them from the activity itself.
        attachmentsForDraft.forEach(a => {
          failedUploads.push({
            name: a.file.name,
            message: UNRESOLVED_ACTIVITY_ATTACHMENT_MESSAGE,
          })
        })
        remainingAttachmentsByDraft[i] = []
        continue
      }

      const results = await Promise.allSettled(
        attachmentsForDraft.map(a => uploadAttachment(createdId as number, a.file))
      )

      const stillFailed: A[] = []
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          onUploadError?.(result.reason)
          failedUploads.push({
            name: attachmentsForDraft[index].file.name,
            message: describeUploadError(result.reason),
          })
          stillFailed.push(attachmentsForDraft[index])
        }
      })
      remainingAttachmentsByDraft[i] = stillFailed
    }
  } catch (error) {
    return { ok: false, error, failedUploads, remainingAttachmentsByDraft }
  }

  return { ok: true, failedUploads, remainingAttachmentsByDraft }
}
